import { useState, useRef, useEffect } from "react";
import { Skeleton } from "@/components/ui/skeleton";

interface LoadingImageProps {
  src: string;
  alt: string;
  className?: string;
  skeletonClassName?: string;
}

/**
 * Image component that shows a skeleton on first load, and seamlessly
 * swaps when `src` changes (no flash — the old image stays visible
 * until the new one finishes loading).
 */
export function LoadingImage({ src, alt, className, skeletonClassName = "w-full aspect-[4/3] rounded-md" }: LoadingImageProps) {
  const [visibleSrc, setVisibleSrc] = useState<string | null>(null);
  const pendingSrc = useRef(src);
  useEffect(() => {
    pendingSrc.current = src;
  });

  const showSkeleton = !visibleSrc;

  return (
    <>
      {showSkeleton && <Skeleton className={skeletonClassName} />}
      {/* Hidden preload image for new src — swaps in once loaded */}
      {src !== visibleSrc && (
        <img
          key={src}
          src={src}
          alt={alt}
          className="hidden"
          onLoad={() => {
            // Only update if this src is still the one we want
            if (pendingSrc.current === src) {
              setVisibleSrc(src);
            }
          }}
        />
      )}
      {visibleSrc && (
        <img
          src={visibleSrc}
          alt={alt}
          className={className}
        />
      )}
    </>
  );
}
